import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getApiUrl } from '../../lib/api';
import { staggerContainer, staggerItem, springs } from '../../lib/animations';
import AnimatedButton from '../AnimatedButton';
import NamePromptModal from '../NamePromptModal';

/* ── Types ── */
interface Strain {
  id?: number;
  name: string;
  type?: string;
  thc_min?: number;
  thc_max?: number;
  cbd_min?: number;
  cbd_max?: number;
  effects?: string[];
  flavors?: string[];
  description?: string;
}

interface MixOption {
  key: string;
  label: string;
  description: string;
  defaultRatio: number;
}

interface RollData {
  strain: Strain | null;
  mix: string;
  mixRatio: number;
  paper: string;
  filter: string;
  rollScore: number;
}

/* ── Constants ── */
const STEPS = [
  'Pick Your Strain',
  'Grind It Up',
  'Choose Your Mix',
  'Paper & Filter',
  'Roll It!',
  'Your Joint',
];

const MIX_OPTIONS: MixOption[] = [
  { key: 'pure', label: 'Pure', description: "100% cannabis. The purist's choice.", defaultRatio: 100 },
  { key: 'tobacco', label: 'Tobacco Spliff', description: '50/50 mix. European style.', defaultRatio: 50 },
  { key: 'herbal', label: 'Herbal Mix', description: 'Cannabis + herbal blend (chamomile, lavender). Smooth and mellow.', defaultRatio: 70 },
  { key: 'moonrock', label: 'Moon Rock', description: 'Cannabis + concentrate + kief coating. For the brave.', defaultRatio: 80 },
];

const PAPER_TYPES = ['Classic White', 'King Size', 'Slim', 'Hemp', 'Rice Paper', 'Blunt Wrap'];
const FILTER_TYPES = ['Cardboard', 'Glass Tip', 'Ceramic', 'No Filter'];

/* ── Helpers ── */
function typeBadgeColor(type?: string | null | any) {
  if (typeof type !== 'string') return 'bg-white/10 text-white/60 border-white/20';
  switch (type.toLowerCase()) {
    case 'sativa': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    case 'indica': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'hybrid': return 'bg-green-500/20 text-green-300 border-green-500/30';
    default: return 'bg-white/10 text-white/60 border-white/20';
  }
}

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse bg-white/5 rounded-xl ${className}`} />;
}

function letterGrade(score: number): string {
  if (score >= 97) return 'A+';
  if (score >= 93) return 'A';
  if (score >= 90) return 'A-';
  if (score >= 87) return 'B+';
  if (score >= 83) return 'B';
  if (score >= 80) return 'B-';
  if (score >= 77) return 'C+';
  if (score >= 73) return 'C';
  if (score >= 70) return 'C-';
  if (score >= 67) return 'D+';
  if (score >= 63) return 'D';
  if (score >= 60) return 'D-';
  return 'F';
}

function gradeColor(grade: string): string {
  if (grade.startsWith('A')) return 'text-[#4ade80]';
  if (grade.startsWith('B')) return 'text-blue-400';
  if (grade.startsWith('C')) return 'text-yellow-400';
  if (grade.startsWith('D')) return 'text-orange-400';
  return 'text-red-400';
}

function calcAestheticsScore(mix: string, paper: string, filter: string): number {
  let score = 50;
  if (mix === 'moonrock') score += 20;
  if (mix === 'pure') score += 10;
  if (mix === 'herbal') score += 5;
  if (paper === 'Hemp') score += 10;
  if (paper === 'Rice Paper') score += 8;
  if (paper === 'King Size') score += 5;
  if (paper === 'Blunt Wrap') score += 7;
  if (filter === 'Glass Tip') score += 15;
  if (filter === 'Ceramic') score += 10;
  if (filter === 'Cardboard') score += 3;
  return Math.min(100, score);
}

function calcStrainQuality(strain: Strain | null): number {
  if (!strain) return 50;
  let score = 60;
  const thcAvg = ((strain.thc_min ?? 0) + (strain.thc_max ?? 20)) / 2;
  score += Math.min(25, thcAvg);
  if (strain.effects && strain.effects.length > 3) score += 10;
  if (strain.flavors && strain.flavors.length > 2) score += 5;
  return Math.min(100, score);
}

/* ── Progress Bar ── */
function ProgressBar({ step }: { step: number }) {
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex flex-col items-center flex-1">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border transition-all duration-300 ${
                i < step
                  ? 'bg-[#4ade80]/20 border-[#4ade80]/50 text-[#4ade80]'
                  : i === step
                  ? 'bg-[#4ade80] border-[#4ade80] text-cannabis-900'
                  : 'bg-white/5 border-white/10 text-white/30'
              }`}
            >
              {i < step ? '\u2713' : i + 1}
            </div>
            <span className={`text-[10px] mt-1 text-center leading-tight hidden sm:block ${
              i <= step ? 'text-cream/70' : 'text-cream/30'
            }`}>
              {label}
            </span>
          </div>
        ))}
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-[#4ade80]/60 rounded-full"
          animate={{ width: `${((step) / (STEPS.length - 1)) * 100}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

/* ── Step Wrapper ── */
function StepWrapper({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

/* ── Step 1: Strain Picker ── */
function StrainPickerStep({
  onSelect,
  selected,
  onNext,
}: {
  onSelect: (s: Strain) => void;
  selected: Strain | null;
  onNext: () => void;
}) {
  const [strains, setStrains] = useState<Strain[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(getApiUrl('/api/strains-data?per_page=500'));
        if (!res.ok) throw new Error();
        const data = await res.json();
        const items: Strain[] = data.items ?? data.strains ?? data.data ?? (Array.isArray(data) ? data : []);
        setStrains(items);
      } catch {
        setStrains([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = strains.filter(s =>
    typeof s.name === 'string' && s.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Pick Your Strain</h3>
      <input
        type="text"
        placeholder="Search strains..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-cannabis-900 border border-cannabis-600 rounded-xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-[#4ade80]/50 mb-4"
      />

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="max-h-64 overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-white/10 pr-1"
        >
          {filtered.length === 0 ? (
            <p className="text-cream/40 text-sm text-center py-8">No strains found.</p>
          ) : (
            filtered.map(strain => (
              <motion.button
                key={strain.name}
                variants={staggerItem}
                onClick={() => onSelect(strain)}
                className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all ${
                  selected?.name === strain.name
                    ? 'bg-[#4ade80]/10 border-[#4ade80]/40 text-cream'
                    : 'bg-white/[0.02] border-white/5 text-cream/70 hover:bg-white/5 hover:border-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-sm flex-1">{strain.name}</span>
                  {strain.type && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadgeColor(strain.type)}`}>
                      {strain.type}
                    </span>
                  )}
                </div>
              </motion.button>
            ))
          )}
        </motion.div>
      )}

      {selected && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-cream font-semibold">{selected.name}</span>
            {selected.type && (
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadgeColor(selected.type)}`}>
                {selected.type}
              </span>
            )}
          </div>
          {(selected.thc_min != null || selected.thc_max != null) && (
            <p className="text-cream/50 text-xs">
              THC: {selected.thc_min ?? '?'}% - {selected.thc_max ?? '?'}%
            </p>
          )}
          {selected.effects && selected.effects.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {selected.effects.slice(0, 5).map(e => (
                <span key={e} className="text-[10px] px-2 py-0.5 rounded-full bg-[#4ade80]/10 text-[#4ade80]/80 border border-[#4ade80]/20">
                  {e}
                </span>
              ))}
            </div>
          )}
        </motion.div>
      )}

      <div className="mt-6 flex justify-end">
        <AnimatedButton onClick={onNext} disabled={!selected}>
          Next
        </AnimatedButton>
      </div>
    </StepWrapper>
  );
}

/* ── Step 2: Grinder ── */
function GrindStep({ onComplete }: { onComplete: () => void }) {
  const [taps, setTaps] = useState(0);
  const [done, setDone] = useState(false);
  const TARGET = 20;

  const handleTap = useCallback(() => {
    if (done) return;
    setTaps(prev => {
      const next = prev + 1;
      if (next >= TARGET) {
        setDone(true);
        setTimeout(onComplete, 1000);
      }
      return next;
    });
  }, [done, onComplete]);

  const progress = Math.min(100, (taps / TARGET) * 100);

  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Grind It Up</h3>
      <div className="flex flex-col items-center gap-6 py-4">
        <motion.button
          onClick={handleTap}
          whileTap={done ? undefined : { scale: 0.9 }}
          animate={done ? { rotate: 360 } : taps > 0 ? { scale: [1, 1.08, 1] } : {}}
          transition={springs.bouncy}
          disabled={done}
          className={`w-40 h-40 rounded-full border-4 flex flex-col items-center justify-center transition-colors ${
            done
              ? 'bg-[#4ade80]/20 border-[#4ade80]/60'
              : 'bg-cannabis-800 border-cannabis-600 hover:border-cannabis-400 active:border-[#4ade80]/50 cursor-pointer'
          }`}
        >
          {/* Inner grinder visual */}
          <div className="relative w-28 h-28 rounded-full overflow-hidden">
            <div className="absolute inset-0 bg-cannabis-700/50 rounded-full" />
            <motion.div
              className="absolute bottom-0 left-0 right-0 bg-[#4ade80]/30 rounded-b-full"
              animate={{ height: `${progress}%` }}
              transition={{ duration: 0.15 }}
            />
            {/* Grinder teeth pattern */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="grid grid-cols-3 gap-2 opacity-30">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="w-2 h-2 bg-white/60 rounded-full" />
                ))}
              </div>
            </div>
          </div>
        </motion.button>

        <div className="text-center">
          {done ? (
            <motion.p
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-[#4ade80] font-bold text-lg"
            >
              Perfectly ground!
            </motion.p>
          ) : (
            <p className="text-cream/60 text-sm">
              Tap to grind! <span className="text-cream font-semibold">{taps}/{TARGET}</span>
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-xs h-2 bg-white/5 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[#4ade80]/60 rounded-full"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.15 }}
          />
        </div>
      </div>
    </StepWrapper>
  );
}

/* ── Step 3: Mix ── */
function MixStep({
  selected,
  ratio,
  onSelect,
  onRatioChange,
  onNext,
}: {
  selected: string;
  ratio: number;
  onSelect: (key: string) => void;
  onRatioChange: (val: number) => void;
  onNext: () => void;
}) {
  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Choose Your Mix</h3>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        {MIX_OPTIONS.map(opt => (
          <motion.button
            key={opt.key}
            variants={staggerItem}
            onClick={() => {
              onSelect(opt.key);
              onRatioChange(opt.defaultRatio);
            }}
            className={`text-left p-4 rounded-xl border transition-all ${
              selected === opt.key
                ? 'bg-[#4ade80]/10 border-[#4ade80]/40'
                : 'bg-white/[0.02] border-white/5 hover:bg-white/5 hover:border-white/10'
            }`}
          >
            <p className="font-semibold text-cream text-sm">{opt.label}</p>
            <p className="text-cream/40 text-xs mt-1">{opt.description}</p>
          </motion.button>
        ))}
      </motion.div>

      {selected && selected !== 'pure' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl"
        >
          <label className="text-cream/60 text-xs block mb-2">
            Cannabis ratio: <span className="text-cream font-semibold">{ratio}%</span>
          </label>
          <input
            type="range"
            min={50}
            max={100}
            value={ratio}
            onChange={e => onRatioChange(Number(e.target.value))}
            className="w-full accent-[#4ade80]"
          />
          <div className="flex justify-between text-[10px] text-cream/30 mt-1">
            <span>50%</span>
            <span>100%</span>
          </div>
        </motion.div>
      )}

      <div className="mt-6 flex justify-end">
        <AnimatedButton onClick={onNext} disabled={!selected}>
          Next
        </AnimatedButton>
      </div>
    </StepWrapper>
  );
}

/* ── Step 4: Paper & Filter ── */
function PaperFilterStep({
  paper,
  filter,
  onPaper,
  onFilter,
  onNext,
}: {
  paper: string;
  filter: string;
  onPaper: (v: string) => void;
  onFilter: (v: string) => void;
  onNext: () => void;
}) {
  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Paper & Filter</h3>

      <div className="mb-6">
        <p className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-3">Paper Type</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PAPER_TYPES.map(p => (
            <motion.button
              key={p}
              whileTap={{ scale: 0.97 }}
              onClick={() => onPaper(p)}
              className={`px-3 py-2.5 rounded-xl border text-sm transition-all ${
                paper === p
                  ? 'bg-[#4ade80]/10 border-[#4ade80]/40 text-cream font-semibold'
                  : 'bg-white/[0.02] border-white/5 text-cream/60 hover:bg-white/5 hover:border-white/10'
              }`}
            >
              {p}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <p className="text-cream/60 text-xs font-semibold uppercase tracking-wider mb-3">Filter Type</p>
        <div className="grid grid-cols-2 gap-2">
          {FILTER_TYPES.map(f => (
            <motion.button
              key={f}
              whileTap={{ scale: 0.97 }}
              onClick={() => onFilter(f)}
              className={`px-3 py-2.5 rounded-xl border text-sm transition-all ${
                filter === f
                  ? 'bg-[#4ade80]/10 border-[#4ade80]/40 text-cream font-semibold'
                  : 'bg-white/[0.02] border-white/5 text-cream/60 hover:bg-white/5 hover:border-white/10'
              }`}
            >
              {f}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <AnimatedButton onClick={onNext} disabled={!paper || !filter}>
          Next
        </AnimatedButton>
      </div>
    </StepWrapper>
  );
}

/* ── Step 5: Roll It ── */
function RollStep({ onComplete }: { onComplete: (score: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rolling, setRolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState('Drag across to roll!');
  const speedSamplesRef = useRef<number[]>([]);
  const lastXRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);

  const processMove = useCallback((clientX: number) => {
    if (!rolling || done || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = (clientX - rect.left) / rect.width;
    const now = performance.now();

    if (lastTimeRef.current > 0) {
      const dt = now - lastTimeRef.current;
      const dx = Math.abs(relativeX - lastXRef.current);
      if (dt > 0) {
        const speed = dx / dt; // normalized speed
        speedSamplesRef.current.push(speed);

        // Feedback based on speed
        if (speed > 0.003) {
          setFeedback('Too fast!');
        } else if (speed < 0.0003) {
          setFeedback('Too slow...');
        } else {
          setFeedback('Perfect pace!');
        }
      }
    }

    lastXRef.current = relativeX;
    lastTimeRef.current = now;

    const newProgress = Math.min(100, relativeX * 100);
    setProgress(newProgress);

    if (newProgress >= 98) {
      setDone(true);
      setRolling(false);

      // Calculate score from speed consistency
      const samples = speedSamplesRef.current;
      if (samples.length < 3) {
        onComplete(50);
        return;
      }
      const avg = samples.reduce((a, b) => a + b, 0) / samples.length;
      const variance = samples.reduce((sum, s) => sum + Math.pow(s - avg, 2), 0) / samples.length;
      const stdDev = Math.sqrt(variance);
      // Lower stddev = more consistent = better score
      const consistencyScore = Math.max(0, 100 - stdDev * 50000);
      // Also penalize if avg speed was too fast or too slow
      const idealSpeed = 0.0012;
      const speedPenalty = Math.min(30, Math.abs(avg - idealSpeed) * 20000);
      const finalScore = Math.max(10, Math.min(100, Math.round(consistencyScore - speedPenalty)));
      onComplete(finalScore);
    }
  }, [rolling, done, onComplete]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (done) return;
    setRolling(true);
    speedSamplesRef.current = [];
    lastXRef.current = 0;
    lastTimeRef.current = 0;
    processMove(e.clientX);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [done, processMove]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    processMove(e.clientX);
  }, [processMove]);

  const handlePointerUp = useCallback(() => {
    if (!done) {
      setRolling(false);
      setProgress(0);
      speedSamplesRef.current = [];
      setFeedback('Drag across to roll!');
    }
  }, [done]);

  const feedbackColor = feedback === 'Perfect pace!'
    ? 'text-[#4ade80]'
    : feedback === 'Too fast!'
    ? 'text-red-400'
    : feedback === 'Too slow...'
    ? 'text-yellow-400'
    : 'text-cream/50';

  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Roll It!</h3>
      <p className="text-cream/40 text-xs mb-6">
        Drag smoothly from left to right. Keep a steady pace for the best score.
      </p>

      {/* Rolling area */}
      <div
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        className={`relative w-full h-32 rounded-2xl border-2 overflow-hidden select-none touch-none ${
          done
            ? 'border-[#4ade80]/40 bg-[#4ade80]/5'
            : rolling
            ? 'border-[#d4a574]/60 bg-cannabis-800 cursor-grabbing'
            : 'border-white/10 bg-cannabis-800 cursor-grab'
        }`}
      >
        {/* Background track */}
        <div className="absolute inset-0 flex items-center px-4">
          <div className="w-full h-3 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#d4a574]/60 via-[#4ade80]/60 to-[#4ade80]/80"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.05 }}
            />
          </div>
        </div>

        {/* Joint forming visual */}
        <div className="absolute inset-0 flex items-center pointer-events-none">
          <motion.div
            className="h-6 rounded-full bg-gradient-to-r from-[#d4a574] to-[#c49660] ml-4 flex items-center"
            animate={{ width: `${Math.max(1, progress * 0.85)}%` }}
            transition={{ duration: 0.05 }}
            style={{ minWidth: progress > 0 ? 20 : 0 }}
          >
            {progress > 5 && (
              <div className="w-2 h-4 bg-[#d4a574]/80 rounded-l-sm ml-auto mr-0 border-l border-white/10" />
            )}
          </motion.div>
        </div>

        {/* Feedback */}
        <div className="absolute bottom-2 left-0 right-0 text-center">
          <motion.span
            key={feedback}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-xs font-semibold ${feedbackColor}`}
          >
            {done ? 'Rolled!' : feedback}
          </motion.span>
        </div>
      </div>

      {/* Percentage indicator */}
      <div className="mt-3 text-center">
        <span className="text-cream/40 text-sm">
          {done ? '100% Complete' : `${Math.round(progress)}%`}
        </span>
      </div>
    </StepWrapper>
  );
}

/* ── Step 6: Results ── */
function ResultStep({
  data,
  onShareGlobally,
  onRollAnother,
  sharing,
  shared,
}: {
  data: RollData;
  onShareGlobally: () => void;
  onRollAnother: () => void;
  sharing: boolean;
  shared: boolean;
}) {
  const aesthetics = calcAestheticsScore(data.mix, data.paper, data.filter);
  const strainQuality = calcStrainQuality(data.strain);
  const overall = Math.round(data.rollScore * 0.4 + aesthetics * 0.3 + strainQuality * 0.3);
  const grade = letterGrade(overall);
  const mixLabel = MIX_OPTIONS.find(m => m.key === data.mix)?.label ?? data.mix;

  return (
    <StepWrapper>
      <h3 className="text-lg font-bold text-cream mb-4">Your Joint</h3>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-cannabis-800 to-cannabis-900 border border-white/10 rounded-2xl p-6 space-y-5"
      >
        {/* Joint visual header */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#d4a574]/10 border border-[#d4a574]/30 flex items-center justify-center">
            <div className="w-10 h-3 rounded-full bg-gradient-to-r from-[#d4a574] to-[#c49660]" />
          </div>
          <div>
            <h4 className="text-cream font-bold text-lg">{data.strain?.name ?? 'Unknown Strain'}</h4>
            <div className="flex items-center gap-2 mt-1">
              {data.strain?.type && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeBadgeColor(data.strain.type)}`}>
                  {data.strain.type}
                </span>
              )}
              <span className="text-cream/40 text-xs">{mixLabel} ({data.mixRatio}%)</span>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/[0.03] rounded-xl p-3">
            <p className="text-cream/40 text-[10px] uppercase tracking-wider">Paper</p>
            <p className="text-cream text-sm font-medium mt-0.5">{data.paper}</p>
          </div>
          <div className="bg-white/[0.03] rounded-xl p-3">
            <p className="text-cream/40 text-[10px] uppercase tracking-wider">Filter</p>
            <p className="text-cream text-sm font-medium mt-0.5">{data.filter}</p>
          </div>
        </div>

        {/* Scores */}
        <div className="space-y-3">
          <ScoreRow label="Roll Technique" score={data.rollScore} />
          <ScoreRow label="Aesthetics" score={aesthetics} />
          <ScoreRow label="Strain Quality" score={strainQuality} />
        </div>

        {/* Overall */}
        <div className="text-center pt-2 border-t border-white/5">
          <p className="text-cream/40 text-xs uppercase tracking-wider mb-1">Overall Score</p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-4xl font-bold text-cream">{overall}</span>
            <span className={`text-3xl font-bold ${gradeColor(grade)}`}>{grade}</span>
          </div>
        </div>
      </motion.div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <AnimatedButton
          onClick={onShareGlobally}
          disabled={sharing || shared}
          loading={sharing}
          className="flex-1"
        >
          {shared ? 'Shared!' : 'Share Globally'}
        </AnimatedButton>
        <AnimatedButton onClick={onRollAnother} variant="secondary" className="flex-1">
          Roll Another
        </AnimatedButton>
      </div>
    </StepWrapper>
  );
}

function ScoreRow({ label, score }: { label: string; score: number }) {
  const grade = letterGrade(score);
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-cream/60 text-xs">{label}</span>
        <span className={`text-xs font-semibold ${gradeColor(grade)}`}>{score} ({grade})</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-[#4ade80]/50"
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
        />
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function RollGameTab() {
  const [step, setStep] = useState(0);
  const [strain, setStrain] = useState<Strain | null>(null);
  const [mix, setMix] = useState('');
  const [mixRatio, setMixRatio] = useState(100);
  const [paper, setPaper] = useState('');
  const [filter, setFilter] = useState('');
  const [rollScore, setRollScore] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState<'share' | null>(null);

  const rollData: RollData = { strain, mix, mixRatio, paper, filter, rollScore };

  const reset = () => {
    setStep(0);
    setStrain(null);
    setMix('');
    setMixRatio(100);
    setPaper('');
    setFilter('');
    setRollScore(0);
    setSharing(false);
    setShared(false);
  };

  const handleShareGlobally = useCallback(async () => {
    const name = localStorage.getItem('rollerName');
    if (!name) {
      setPendingAction('share');
      setShowNamePrompt(true);
      return;
    }

    setSharing(true);
    try {
      const countryRaw = localStorage.getItem('jrater_country');
      let country = '';
      if (countryRaw) {
        try {
          const parsed = JSON.parse(countryRaw);
          country = parsed.code ?? parsed.country ?? '';
        } catch { /* ignore */ }
      }

      const aesthetics = calcAestheticsScore(mix, paper, filter);
      const strainQuality = calcStrainQuality(strain);
      const overall = Math.round(rollScore * 0.4 + aesthetics * 0.3 + strainQuality * 0.3);

      await fetch(getApiUrl('/api/virtual-joints'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creator_name: name,
          country,
          strain_name: strain?.name ?? 'Unknown',
          strain_type: strain?.type ?? '',
          mix_type: mix,
          mix_ratio: mixRatio,
          paper_type: paper,
          filter_type: filter,
          roll_score: rollScore,
          aesthetics_score: aesthetics,
          strain_quality_score: strainQuality,
          overall_score: overall,
        }),
      });
      setShared(true);
    } catch {
      // Silently fail
    } finally {
      setSharing(false);
    }
  }, [mix, mixRatio, paper, filter, rollScore, strain]);

  useEffect(() => {
    if (pendingAction === 'share' && localStorage.getItem('rollerName')) {
      setPendingAction(null);
      handleShareGlobally();
    }
  }, [pendingAction, handleShareGlobally]);

  return (
    <div className="max-w-lg mx-auto">
      <ProgressBar step={step} />

      <AnimatePresence mode="wait">
        {step === 0 && (
          <StrainPickerStep
            key="strain"
            selected={strain}
            onSelect={setStrain}
            onNext={() => setStep(1)}
          />
        )}
        {step === 1 && (
          <GrindStep
            key="grind"
            onComplete={() => setStep(2)}
          />
        )}
        {step === 2 && (
          <MixStep
            key="mix"
            selected={mix}
            ratio={mixRatio}
            onSelect={setMix}
            onRatioChange={setMixRatio}
            onNext={() => setStep(3)}
          />
        )}
        {step === 3 && (
          <PaperFilterStep
            key="paper"
            paper={paper}
            filter={filter}
            onPaper={setPaper}
            onFilter={setFilter}
            onNext={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <RollStep
            key="roll"
            onComplete={(score) => {
              setRollScore(score);
              setStep(5);
            }}
          />
        )}
        {step === 5 && (
          <ResultStep
            key="result"
            data={rollData}
            onShareGlobally={handleShareGlobally}
            onRollAnother={reset}
            sharing={sharing}
            shared={shared}
          />
        )}
      </AnimatePresence>

      <NamePromptModal
        open={showNamePrompt}
        onClose={() => { setShowNamePrompt(false); setPendingAction(null); }}
        onSubmit={() => setShowNamePrompt(false)}
      />
    </div>
  );
}
